import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full bg-white border border-[#E8E1D8] px-3 py-2 text-sm outline-none focus:border-[#1D1D1B]";

export function AdminCollections() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", image: "" });
  const load = () => api.get("/admin/collections").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/collections", form); setForm({ name: "", description: "", image: "" }); toast.success("Aangemaakt"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { await api.delete(`/admin/collections/${id}`); load(); };
  const toggle = async (c) => { await api.put(`/admin/collections/${c.id}`, { published: !c.published }); load(); };
  return (
    <div data-testid="admin-collections">
      <h1 className="heading-x text-2xl mb-6">COLLECTIES</h1>
      <div className="border border-[#E8E1D8] bg-white p-4 mb-6 grid md:grid-cols-4 gap-3 items-end">
        <label><span className="label-x">NAAM</span><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="col-name" /></label>
        <label><span className="label-x">AFBEELDING URL</span><input className={inp} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></label>
        <label className="md:col-span-1"><span className="label-x">OMSCHRIJVING</span><input className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        <button onClick={create} className="btn-x" data-testid="col-create">TOEVOEGEN</button>
      </div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-3">
            <span className="flex-1 text-sm">{c.name} <span className="label-x">/{c.slug}</span> {c.demo && <span className="label-x">DEMO</span>}</span>
            <button onClick={() => toggle(c)} className="label-x">{c.published ? "GEPUBLICEERD" : "VERBORGEN"}</button>
            <button onClick={() => del(c.id)} className="label-x">VERWIJDER</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminLookbooks() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/lookbooks").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => { const { data } = await api.post("/admin/lookbooks", { title: "Nieuwe lookbook", cover: "", blocks: [] }); setEdit(data); load(); };
  const save = async () => { await api.put(`/admin/lookbooks/${edit.id}`, edit); toast.success("Opgeslagen"); setEdit(null); load(); };
  const del = async (id) => { await api.delete(`/admin/lookbooks/${id}`); load(); };
  return (
    <div data-testid="admin-lookbooks">
      <div className="flex justify-between mb-6"><h1 className="heading-x text-2xl">LOOKBOOKS</h1><button onClick={create} className="btn-x text-[0.65rem]">NIEUWE LOOKBOOK</button></div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((l) => (
          <div key={l.id} className="flex items-center gap-4 p-3">
            <span className="flex-1 text-sm">{l.title} <span className="label-x">/{l.slug}</span></span>
            <button onClick={() => setEdit(JSON.parse(JSON.stringify(l)))} className="label-x">BEWERK</button>
            <button onClick={() => del(l.id)} className="label-x">VERWIJDER</button>
          </div>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 bg-[#1D1D1B]/50 z-50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-[#F7F5F1] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
            <p className="heading-x text-sm mb-4">LOOKBOOK BEWERKEN</p>
            <label className="block mb-3"><span className="label-x">TITEL</span><input className={inp} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></label>
            <label className="block mb-3"><span className="label-x">COVER URL</span><input className={inp} value={edit.cover || ""} onChange={(e) => setEdit({ ...edit, cover: e.target.value })} /></label>
            <label className="block mb-3"><span className="label-x">BLOKKEN (JSON)</span><textarea rows={8} className={`${inp} font-mono text-xs`} defaultValue={JSON.stringify(edit.blocks || [], null, 2)} onBlur={(e) => { try { setEdit({ ...edit, blocks: JSON.parse(e.target.value) }); } catch { toast.error("Ongeldige JSON"); } }} /></label>
            <label className="flex items-center gap-2 label-x mb-4"><input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} /> GEPUBLICEERD</label>
            <button onClick={save} className="btn-x">OPSLAAN</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminPages() {
  const [pages, setPages] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/pages").then(({ data }) => setPages(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => { await api.put(`/admin/pages/${edit.slug}`, edit); toast.success("Opgeslagen"); setEdit(null); load(); };
  return (
    <div data-testid="admin-pages">
      <h1 className="heading-x text-2xl mb-6">CONTENT PAGINA'S</h1>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {pages.map((p) => (
          <div key={p.slug} className="flex items-center gap-4 p-3">
            <span className="flex-1 text-sm">{p.title} <span className="label-x">/{p.slug}</span></span>
            <button onClick={() => setEdit({ ...p })} className="label-x" data-testid={`edit-page-${p.slug}`}>BEWERK</button>
          </div>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 bg-[#1D1D1B]/50 z-50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-[#F7F5F1] p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
            <p className="heading-x text-sm mb-4">BEWERK {edit.slug}</p>
            <label className="block mb-3"><span className="label-x">TITEL</span><input className={inp} value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="page-title" /></label>
            <label className="block mb-4"><span className="label-x">INHOUD</span><textarea rows={12} className={inp} value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} data-testid="page-body" /></label>
            <button onClick={save} className="btn-x" data-testid="save-page-btn">OPSLAAN</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminDiscounts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percentage", value: 10, min_order: 0, active: true });
  const load = () => api.get("/admin/discounts").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/discounts", form); setForm({ code: "", type: "percentage", value: 10, min_order: 0, active: true }); toast.success("Aangemaakt"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { await api.delete(`/admin/discounts/${id}`); load(); };
  const toggle = async (d) => { await api.put(`/admin/discounts/${d.id}`, { active: !d.active }); load(); };
  return (
    <div data-testid="admin-discounts">
      <h1 className="heading-x text-2xl mb-6">KORTINGEN</h1>
      <div className="border border-[#E8E1D8] bg-white p-4 mb-6 grid md:grid-cols-5 gap-3 items-end">
        <label><span className="label-x">CODE</span><input className={inp} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="disc-code" /></label>
        <label><span className="label-x">TYPE</span><select className={inp} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="percentage">%</option><option value="fixed">€ VAST</option></select></label>
        <label><span className="label-x">WAARDE</span><input type="number" className={inp} value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) })} data-testid="disc-value" /></label>
        <label><span className="label-x">MIN. BESTELLING (€)</span><input type="number" className={inp} value={form.min_order} onChange={(e) => setForm({ ...form, min_order: parseFloat(e.target.value) })} /></label>
        <button onClick={create} className="btn-x" data-testid="disc-create">TOEVOEGEN</button>
      </div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((d) => (
          <div key={d.id} className="flex items-center gap-4 p-3">
            <span className="font-mono text-sm flex-1">{d.code} — {d.type === "percentage" ? `${d.value}%` : `€${d.value}`} {d.demo && <span className="label-x">DEMO</span>}</span>
            <span className="label-x">{d.used_count || 0}x GEBRUIKT</span>
            <button onClick={() => toggle(d)} className="label-x">{d.active ? "ACTIEF" : "INACTIEF"}</button>
            <button onClick={() => del(d.id)} className="label-x">VERWIJDER</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminReviews() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = async (id, status) => { await api.patch(`/admin/reviews/${id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  return (
    <div data-testid="admin-reviews">
      <h1 className="heading-x text-2xl mb-6">RECENSIES</h1>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((r) => (
          <div key={r.id} className="p-4" data-testid={`review-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div><p className="text-sm font-medium">{r.title} — {r.rating}★ {r.verified_purchase && <span className="label-x">GEVERIFIEERD</span>}</p><p className="text-sm text-[#77736D] mt-1">{r.text}</p><p className="label-x mt-1">{r.name} · {r.status.toUpperCase()}</p></div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => set(r.id, "approved")} className="label-x" data-testid={`approve-${r.id}`}>GOEDKEUREN</button>
                <button onClick={() => set(r.id, "rejected")} className="label-x">AFWIJZEN</button>
                <button onClick={() => del(r.id)} className="label-x">VERWIJDER</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="label-x p-8 text-center">GEEN REVIEWS</p>}
      </div>
    </div>
  );
}

export function AdminCustomers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-customers">
      <div className="flex justify-between mb-6"><h1 className="heading-x text-2xl">KLANTEN</h1><a href={`${api.defaults.baseURL}/admin/export/customers.csv`} className="btn-x-outline text-[0.65rem]">EXPORT CSV</a></div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-3 text-sm">
            <span className="flex-1">{c.name || "—"} <span className="text-[#77736D]">{c.email}</span></span>
            <span className="label-x">{c.order_count} BESTELLINGEN</span>
          </div>
        ))}
        {items.length === 0 && <p className="label-x p-8 text-center">NOG GEEN KLANTEN</p>}
      </div>
    </div>
  );
}
